import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import {
  canCancelRecoveryRequest,
  canCompleteRecoveryRequest,
  canDecideRecoveryRequest,
  formatDateTime,
  getRecoveryRequestTiming,
  requestIsActive,
  requestStatusLabel,
} from './recoveryUi.ts';
import {
  assertPublicRecoveryJwk,
  assertRequestBoundEnvelope,
} from './payloadSafety.ts';
import type {
  CreateRecoveryRequestInput,
  DecideRecoveryRequestInput,
  GetRecoveryTargetPublicKey,
  GetRequestBoundSealedShare,
  RecoveryCircle,
  RecoveryDecision,
  RecoveryDevice,
  RecoveryRequest,
} from './types.ts';

function useCurrentTime(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [enabled]);
  return now;
}

interface RecoveryRequestCardProps {
  request: RecoveryRequest;
  now: number;
  getRequestBoundSealedShare?: GetRequestBoundSealedShare;
  onDecide: (
    request: RecoveryRequest,
    input: DecideRecoveryRequestInput
  ) => Promise<void>;
  onCancel: (request: RecoveryRequest) => Promise<void>;
  onComplete?: (request: RecoveryRequest) => Promise<void>;
}

function RecoveryRequestCard({
  request,
  now,
  getRequestBoundSealedShare,
  onDecide,
  onCancel,
  onComplete,
}: RecoveryRequestCardProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timing = getRecoveryRequestTiming(request, now);
  const canApprove = canDecideRecoveryRequest(request, 'approve', now);
  const canDeny = canDecideRecoveryRequest(request, 'deny', now);
  const canReport = canDecideRecoveryRequest(request, 'report', now);
  const canCancel = canCancelRecoveryRequest(request);
  const canComplete = canCompleteRecoveryRequest(request);

  const run = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : `Could not ${label} this request.`
      );
    } finally {
      setBusy(null);
    }
  };

  const decide = (decision: RecoveryDecision) =>
    run(decision, async () => {
      if (decision === 'approve') {
        if (!getRequestBoundSealedShare) {
          throw new Error(
            'Approval is unavailable until device crypto can seal a request-bound share.'
          );
        }
        const supplied = await getRequestBoundSealedShare(request);
        if (!supplied) {
          throw new Error(
            'Device crypto did not return a sealed share. No approval was sent.'
          );
        }
        if (supplied.requestId !== request.id) {
          throw new Error(
            'Device crypto returned custody for a different request. No approval was sent.'
          );
        }
        assertRequestBoundEnvelope(supplied.sealedShare, request.id);
        await onDecide(request, {
          decision: 'approve',
          sealedShare: supplied.sealedShare,
        });
        return;
      }
      await onDecide(request, { decision });
    });

  return (
    <Card className="recovery-request-card">
      <Card.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <Card.Title as="h3" className="h5 mb-1">
              Recovery drill on {request.targetLabel}
            </Card.Title>
            {request.ownerLabel && request.viewerRole === 'keyholder' && (
              <p className="text-body-secondary mb-0">
                Circle owner: {request.ownerLabel}
              </p>
            )}
          </div>
          <Badge
            bg={
              request.status === 'ready'
                ? 'success'
                : requestIsActive(request)
                  ? 'warning'
                  : 'secondary'
            }
            text={requestIsActive(request) && request.status !== 'ready' ? 'dark' : undefined}
          >
            {requestStatusLabel(request.status)}
          </Badge>
        </div>

        <p className="recovery-timing mt-3" data-phase={timing.phase}>
          <strong>{timing.summary}</strong>
        </p>
        <p>
          <strong>{request.approvalCount} of {request.approvalsRequired}</strong>{' '}
          approvals received. Counts are shown without exposing a private roster.
        </p>
        <p className="recovery-fingerprint">
          Target fingerprint:{' '}
          <strong>{request.targetFingerprint || 'not provided by server'}</strong>
        </p>

        <details className="mb-3">
          <summary>Request timing and scope</summary>
          <dl className="row mt-3 mb-0 recovery-details-list">
            <dt className="col-sm-4">Created</dt>
            <dd className="col-sm-8">{formatDateTime(request.createdAt)}</dd>
            <dt className="col-sm-4">Approvals open</dt>
            <dd className="col-sm-8">{formatDateTime(request.availableAt)}</dd>
            <dt className="col-sm-4">Expires</dt>
            <dd className="col-sm-8">{formatDateTime(request.expiresAt)}</dd>
            <dt className="col-sm-4">Request ID</dt>
            <dd className="col-sm-8 recovery-fingerprint">{request.id}</dd>
          </dl>
          <p className="small mb-0">
            This is a lockout drill tied to the named request and registered
            target device. It does not grant message access to a keyholder.
          </p>
        </details>

        {request.viewerRole === 'keyholder' &&
          canApprove &&
          !getRequestBoundSealedShare && (
            <Alert variant="warning">
              Approve is disabled because no crypto callback can produce a
              request-bound sealed share. Deny and Report remain independent
              decisions when allowed by the server state.
            </Alert>
          )}

        {request.viewerRole === 'keyholder' && (
          <div
            className="d-flex flex-wrap gap-2"
            role="group"
            aria-label={`Decision for recovery request ${request.id}`}
          >
            <Button
              disabled={
                Boolean(busy) || !canApprove || !getRequestBoundSealedShare
              }
              onClick={() => decide('approve')}
            >
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={Boolean(busy) || !canDeny}
              onClick={() => decide('deny')}
            >
              {busy === 'deny' ? 'Denying…' : 'Deny'}
            </Button>
            <Button
              variant="outline-danger"
              disabled={Boolean(busy) || !canReport}
              onClick={() => decide('report')}
            >
              {busy === 'report' ? 'Reporting…' : 'Report suspicious request'}
            </Button>
          </div>
        )}

        {request.viewerRole === 'owner' && (canCancel || canComplete) && (
          <div
            className="d-flex flex-wrap gap-2"
            role="group"
            aria-label={`Owner controls for recovery request ${request.id}`}
          >
            {canCancel && (
              <Button
                variant="outline-danger"
                disabled={Boolean(busy)}
                onClick={() => run('cancel', () => onCancel(request))}
              >
                {busy === 'cancel' ? 'Cancelling…' : 'Cancel drill'}
              </Button>
            )}
            {canComplete && (
              <Button
                variant="success"
                disabled={Boolean(busy) || !onComplete}
                onClick={() =>
                  onComplete &&
                  run('complete', () => onComplete(request))
                }
              >
                {busy === 'complete'
                  ? 'Completing…'
                  : onComplete
                    ? 'Complete target-device drill'
                    : 'Completion integration unavailable'}
              </Button>
            )}
          </div>
        )}
        {request.viewerRole === 'owner' && canComplete && (
          <p className="small text-body-secondary mt-2 mb-0">
            {onComplete
              ? 'Completion runs the parent target-device callback before recording server state. This card does not itself restore or decrypt messages.'
              : 'Completion is disabled until parent device crypto can consume the request-bound releases on the registered target device.'}
          </p>
        )}

        {request.myDecision && (
          <p className="mt-3 mb-0">
            Your recorded decision: <strong>{request.myDecision}</strong>.
          </p>
        )}
        <div aria-live="polite" aria-atomic="true">
          {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
        </div>
      </Card.Body>
    </Card>
  );
}

interface RequestListProps
  extends Omit<RecoveryRequestCardProps, 'request' | 'now'> {
  requests: RecoveryRequest[];
  emptyMessage: string;
}

export function RecoveryRequestList({
  requests,
  emptyMessage,
  ...actions
}: RequestListProps) {
  const now = useCurrentTime(requests.some(requestIsActive));
  if (!requests.length) return <p className="recovery-empty">{emptyMessage}</p>;
  return (
    <Row className="g-3">
      {requests.map((request) => (
        <Col xl={6} key={request.id}>
          <RecoveryRequestCard request={request} now={now} {...actions} />
        </Col>
      ))}
    </Row>
  );
}

export interface RecoveryRequestsProps {
  circle: RecoveryCircle | null;
  devices: RecoveryDevice[];
  requests: RecoveryRequest[];
  getTargetPublicKeyJwk?: GetRecoveryTargetPublicKey;
  getRequestBoundSealedShare?: GetRequestBoundSealedShare;
  onCreate: (input: CreateRecoveryRequestInput) => Promise<void>;
  onDecide: (
    request: RecoveryRequest,
    input: DecideRecoveryRequestInput
  ) => Promise<void>;
  onCancel: (request: RecoveryRequest) => Promise<void>;
  onComplete?: (request: RecoveryRequest) => Promise<void>;
}

export default function RecoveryRequests({
  circle,
  devices,
  requests,
  getTargetPublicKeyJwk,
  getRequestBoundSealedShare,
  onCreate,
  onDecide,
  onCancel,
  onComplete,
}: RecoveryRequestsProps) {
  const activeDevices = useMemo(
    () => devices.filter((device) => device.status !== 'revoked'),
    [devices]
  );
  const [targetDeviceId, setTargetDeviceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRequests = requests.filter(requestIsActive);
  const circleCanRunDrill =
    circle?.status === 'active' && !circle.rotationRequired;

  useEffect(() => {
    if (
      targetDeviceId &&
      !activeDevices.some((device) => device.id === targetDeviceId)
    ) {
      setTargetDeviceId('');
    }
  }, [activeDevices, targetDeviceId]);

  const createDrill = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!circle || !circleCanRunDrill) {
      setError('An active, current recovery circle is required for a drill.');
      return;
    }
    const target = activeDevices.find(
      (device) => device.id === targetDeviceId
    );
    if (!target) {
      setError('Choose a registered target device.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (!getTargetPublicKeyJwk) {
        throw new Error(
          'A request-specific target key is required. Device custody keys are not reused for drills.'
        );
      }
      const targetPublicKeyJwk = await getTargetPublicKeyJwk(target);
      if (!targetPublicKeyJwk?.kty) {
        throw new Error(
          'The target device has no usable public key. No drill was created.'
        );
      }
      assertPublicRecoveryJwk(targetPublicKeyJwk, 'The recovery target key');
      await onCreate({
        circleId: circle.id,
        type: 'lockout',
        targetDeviceId: target.id,
        targetPublicKeyJwk,
      });
      setTargetDeviceId('');
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not create the recovery drill.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="recovery-requests-heading">
      <h2 id="recovery-requests-heading" className="h3">
        Requests
      </h2>
      <Card className="mb-4">
        <Card.Body>
          <Card.Title as="h3" className="h5">
            Recovery drill on a registered device
          </Card.Title>
          <Alert variant="info">
            This first slice exercises authenticated request, delay, and
            approval states. It is not proof-complete lost-device recovery and
            does not claim to restore messages by itself.
          </Alert>
          <Form onSubmit={createDrill}>
            <Row className="align-items-end g-3">
              <Col md={7}>
                <Form.Group controlId="recovery-drill-device">
                  <Form.Label>Target device</Form.Label>
                  <Form.Select
                    value={targetDeviceId}
                    disabled={!circleCanRunDrill || !activeDevices.length}
                    onChange={(event) => setTargetDeviceId(event.target.value)}
                  >
                    <option value="">Choose a registered device</option>
                    {activeDevices.map((device) => (
                      <option value={device.id} key={device.id}>
                        {device.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button
                  type="submit"
                  disabled={busy || !circleCanRunDrill || !targetDeviceId}
                >
                  {busy ? 'Starting drill…' : 'Start recovery drill'}
                </Button>
              </Col>
            </Row>
          </Form>
          {!circleCanRunDrill && (
            <p className="small text-body-secondary mt-2 mb-0">
              Create and activate a current personal-keyholder circle before
              starting a drill.
            </p>
          )}
          {circleCanRunDrill && !activeDevices.length && (
            <p className="small text-body-secondary mt-2 mb-0">
              Register a target device first.
            </p>
          )}
          <div aria-live="polite" aria-atomic="true">
            {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
          </div>
        </Card.Body>
      </Card>

      <h3 className="h5">Open requests</h3>
      <RecoveryRequestList
        requests={activeRequests}
        emptyMessage="There are no open recovery requests."
        getRequestBoundSealedShare={getRequestBoundSealedShare}
        onDecide={onDecide}
        onCancel={onCancel}
        onComplete={onComplete}
      />
    </section>
  );
}
